import { pixiGetDt } from '../../lib/pixi/pixiTools';
import { arrayReverseIterate } from '../../lib/util/jsTools';
import { Ticker } from '../../odie';
import { ParticleEmitter, ParticleEmitterOptions } from './ParticleEmitter';

// private
type EmitterEntry = {
  emitter: ParticleEmitter;
  keepAlive?: boolean;
};

/*
    particle emitter
*/
export class ParticlesPlugin {
  // fields
  //-------------------------------------------------------------------------
  // state
  private readonly _emitters: EmitterEntry[] = [];

  // impl
  //-------------------------------------------------------------------------
  public update(): void {
    // get dt
    const dt = Ticker.system.deltaMS / 1000;

    // step emitters
    this._stepEmitters(dt);

    // log emitter leaks
    //console.log('ParticlesPlugin.emitters', this._emitters.length);
  }

  // api
  //-------------------------------------------------------------------------
  public create(
    options: ParticleEmitterOptions,
    keepAlive?: boolean,
  ): ParticleEmitter {
    // create
    const emitter = new ParticleEmitter(options);

    // add emitter
    this.add(emitter, keepAlive);

    return emitter;
  }

  /**
   * Register an emitter that was created earlier and has since completed, so
   * it runs again with its particle buffer intact. Recreating the emitter
   * instead reallocates every particle and the two views each one carries.
   *
   * The caller is expected to `start()` it in the same frame: an entry whose
   * emitter is still completed is swept on the next update.
   */
  public add(emitter: ParticleEmitter, keepAlive?: boolean) {
    // ignore emitters already stepping
    if (this._emitters.some((entry) => entry.emitter === emitter)) return;

    this._emitters.push({ emitter, keepAlive });
  }

  public destroy(emitter: ParticleEmitter) {
    // get index
    const index = this._emitters.findIndex(
      (entry) => emitter === entry.emitter,
    );
    if (index >= 0) this._destroyEntry(index);
  }

  /**
   * Complete and drop every emitter. Emitters with no `emitterDuration` never
   * complete on their own, so this is required on teardown to stop them
   * leaking into the next session.
   */
  public destroyAll() {
    // complete each emitter
    for (const entry of this._emitters) entry.emitter.complete();

    // clear list
    this._emitters.length = 0;
  }

  // private: stppers
  //-------------------------------------------------------------------------
  private _stepEmitters(dt: number) {
    // step emitters, reverse iterate to allow safe removal
    arrayReverseIterate(this._emitters, (entry, i) => {
      const emitter = entry.emitter;
      // if completed
      if (emitter.completed) {
        // destroy if not keeping alive
        if (!entry.keepAlive) {
          this._destroyEntry(i);
        }
        // else step
      } else {
        emitter.step(dt);
      }
    });
  }

  // private: support
  //-------------------------------------------------------------------------
  public _destroyEntry(index: number) {
    // complete emitter
    this._emitters[index].emitter.complete();

    // remove from list
    this._emitters.splice(index, 1);
  }
}
